import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, AlertCircle } from 'lucide-react';
import { useState } from 'react';

import { dataflowApi } from '@/services/api/dataflow';
import { useDataFlowStore } from '@/stores/dataflowStore';
import type { Template } from '@/types/dataflow';

function nextDefaultTemplateName(templates: Template[]): string {
  const used = new Set(
    templates
      .map((item) => String(item.name || "").trim())
      .filter(Boolean),
  );
  const base = "new_template";
  if (!used.has(base)) return base;
  let index = 2;
  while (index < 10000) {
    const candidate = `${base}_${index}`;
    if (!used.has(candidate)) return candidate;
    index += 1;
  }
  return `${base}_${Date.now()}`;
}

function formatTemplateTriggers(template: Template): string {
  const modes: string[] = [];
  if (template.schedule_kind) {
    const time = template.schedule_config_json?.time;
    modes.push(time ? `${template.schedule_kind} @ ${time}` : String(template.schedule_kind));
  } else {
    modes.push("manual");
  }
  if ((template.depends_on_template_ids || []).length > 0) {
    modes.push("upstream");
  }
  return modes.join(" + ");
}

function formatUpstreamTemplates(
  template: Template,
  templateById: Map<number, Template>,
): string {
  const depIds = template.depends_on_template_ids || [];
  if (depIds.length === 0) return "none";
  return depIds
    .map((depId) => {
      const upstream = templateById.get(depId);
      return upstream ? `${upstream.name} (#${depId})` : `#${depId}`;
    })
    .join(", ");
}

export function TemplateList() {
  const { selectedTemplateId, setSelectedTemplate } = useDataFlowStore();
  const queryClient = useQueryClient();
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['dataflow', 'templates'],
    queryFn: () => dataflowApi.getTemplates(),
  });
  const templateById = new Map(templates.map((template) => [template.id, template]));

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dataflowApi.deleteTemplate(id),
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['dataflow', 'templates'] });
      setSelectedTemplate(null);
    },
    onError: (error: Error) => {
      setDeleteError(error.message || '删除失败');
    },
  });

  const handleDelete = (e: React.MouseEvent, id: string, templateName: string) => {
    e.stopPropagation();
    if (confirm(`确定要删除模板 "${templateName}" 吗？`)) {
      setDeleteError(null);
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="h-full overflow-auto p-3">
      <button
        onClick={() => {
          const name = nextDefaultTemplateName(templates);
          setSelectedTemplate("new");
          // Pre-fill draft name via global defaults handshake:
          // keep name hint in sessionStorage for config panel only.
          sessionStorage.setItem("dataflow:new-template-name", name);
        }}
        disabled={isLoading}
        className="w-full mb-3 py-2 bg-[#0ea5e9] text-white text-xs rounded hover:bg-[#0284c7] transition-colors disabled:opacity-50"
      >
        + New Template
      </button>

      {isLoading && (
        <div className="rounded border border-[var(--border)] bg-[#0f1115] p-3 text-xs text-[#64748b]">
          Loading templates...
        </div>
      )}

      {!isLoading && templates.length === 0 && (
        <div className="rounded border border-[var(--border)] bg-[#0f1115] p-3 text-xs text-[#64748b]">
          No templates yet. Create one to start the manual chain.
        </div>
      )}

      {deleteError && (
        <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200 flex items-start gap-2">
          <AlertCircle className="size-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">删除失败</div>
            <div>{deleteError}</div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {templates.map(template => (
          <div
            key={template.id}
            onClick={() => setSelectedTemplate(String(template.id))}
            className={`p-3 rounded cursor-pointer border transition-colors group ${
              selectedTemplateId === String(template.id)
                ? 'bg-[#1a1d24] border-[#7dd3fc]'
                : 'bg-[#0f1115] border-[var(--border)] hover:border-[#475569]'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-[#7dd3fc] font-medium text-xs">{template.name}</span>
                <span
                  className={`text-[9px] px-1.5 py-0.5 rounded ${
                    template.status === 'ACTIVE'
                      ? 'bg-[#166534] text-[#86efac]'
                      : 'bg-[#374151] text-[#9ca3af]'
                  }`}
                >
                  {template.status}
                </span>
              </div>
              <button
                onClick={(e) => handleDelete(e, String(template.id), template.name)}
                disabled={deleteMutation.isPending}
                className="opacity-0 group-hover:opacity-100 p-1 text-[#64748b] hover:text-[#dc2626] transition-all"
                title="删除模板"
              >
                <Trash2 className="size-3" />
              </button>
            </div>
            <div className="text-[#64748b] text-[10px]">
              ID: {template.id}
            </div>
            <div className="text-[#64748b] text-[10px] mt-1">
              Trigger: {formatTemplateTriggers(template)}
            </div>
            <div className="text-[#64748b] text-[10px] mt-1 truncate" title={formatUpstreamTemplates(template, templateById)}>
              Upstream: {formatUpstreamTemplates(template, templateById)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
